/* globals window */

import React, { useState } from 'react';
import { connect } from 'react-redux';
import { css } from '@emotion/react';
import ExternalLinkSVG from '@birdi/icons/external-link.svg';
import * as Popover from '@radix-ui/react-popover';
import { hasPermission } from '@birdi/utils/src/helpers';
import { auth, stroot } from '@birdi/js-sdk';
import SignOutSVG from '@birdi/icons/log-out.svg';
import ContactUsSVG from '@birdi/icons/menu-contactus2.svg';
import SuggestSVG from '@birdi/icons/menu-suggest2.svg';
import { BirdiTheme, blocks } from '@birdi/theme';
import { TopMenuButton } from '../side-menu/side-bar-utils';
import { FeatureFlagNames } from '@birdi/js-sdk/src/public/organisation-features';
import { useMetaSelector } from '../../meta/reducer';

const avatarButtonCSS = (url: string | null) => (theme: BirdiTheme) => css`
  ${!!url &&
  css`
    background-image: url('${url}');
    background-position: center;
    background-size: cover;
  `}
  background-color: ${theme.flora};
  color: white;
  width: 3em;
  height: 3em;
  border-radius: 3em;
`;

const linkStyle = (theme: BirdiTheme) => css`
  // vertical-align: center;
  color: ${theme.midnight};
  padding: 0.5em;
  width: 95%;
  border-radius: 3px;
  &:hover {
    background: ${theme.lightOcean};
  }
  .icon-external {
    font-size: 0.75em;
    color: black;
    margin-left: 0.5em;
  }
  .icon-comment {
    font-size: 1.5em;
    margin-right: 0.5em;
    vertical-align: middle;
  }
`;

const breakLineStyle = (theme: BirdiTheme) => css`
  height: 2px;
  background: ${theme.mono20};
  width: 95%;
  margin: 0.25em 0;
`;

export const AvatarButton = () => {
  const meta = useMetaSelector();
  const [open, setOpen] = useState(false);

  const costCalculator = meta.orgFeatureFlags.includes(
    FeatureFlagNames.GigapixelCalculator,
  );
  const jobNotif = meta.jobNotifications;

  const isOrganisationAdmin = meta.permissions.includes(
    'organisation:user:create',
  );
  const isWorkspaceAdmin = meta.permissions.includes('workspace:user:create');
  const canEditWorkspace = meta.permissions.includes('workspace:edit');

  const isPrimaryOrg = !meta.workspace;
  const workspaceEnable =
    meta.features?.includes('add-workspaces') &&
    isPrimaryOrg &&
    isWorkspaceAdmin;
  const hasMissionsAccess = meta.orgFeatureFlags.includes(
    FeatureFlagNames.Missions,
  );

  const closeMenu = () => setOpen(false);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button type="button" css={avatarButtonCSS(meta.profilePic)}>
          {!meta.profilePic && meta.fname && meta.fname[0]}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={8}
          css={(theme: BirdiTheme) => [
            blocks.radixDropdownAnimation,
            css`
              min-width: 32em;
              color: ${theme.midnight};
              background: white;
              box-shadow: 0 0 10px ${theme.mono40};
              border-radius: 5px;
              overflow: hidden;
              z-index: 1000;

              .divider {
                border-top: 2px solid ${theme.mono20};
              }

              .item {
                flex: 1;
                white-space: nowrap;
                text-align: left;
                border-radius: 3px;
                &:hover {
                  background: ${theme.lightOcean};
                }
                &:disabled {
                  background: ${theme.mono0};
                  cursor: default;
                }
              }
            `,
          ]}
        >
          <div
            css={css`
              display: flex;
              flex-direction: row;
              padding: 1em;
              .header-item {
                font-weight: bold;
                padding: 0.5rem;
              }
            `}
          >
            <div
              css={css`
                display: flex;
                flex: 1;
                flex-direction: column;
                justify-content: flex-start;
                align-items: flex-start;
              `}
            >
              <span className="header-item">Account</span>
              <div css={breakLineStyle} />
              <TopMenuButton
                link="/admin-general"
                label="Administration"
                closeMenu={closeMenu}
                disabled={!canEditWorkspace}
              />
              {isOrganisationAdmin && isPrimaryOrg && (
                <TopMenuButton
                  link="/admin-billing"
                  label="Billing"
                  closeMenu={closeMenu}
                />
              )}
              {isOrganisationAdmin && isPrimaryOrg && (
                <TopMenuButton
                  link="/admin-plan"
                  label="Plans & Usage"
                  closeMenu={closeMenu}
                />
              )}
              <TopMenuButton
                link={`/profile-general/${meta.scopeId}`}
                label="My Profile"
                closeMenu={closeMenu}
              />
              <TopMenuButton
                link="/admin-users"
                label="User Management"
                closeMenu={closeMenu}
                disabled={!isWorkspaceAdmin}
              />
              {workspaceEnable && (
                <TopMenuButton
                  link="/admin-workspaces"
                  label="Workspaces"
                  closeMenu={closeMenu}
                />
              )}
              {hasMissionsAccess && (
                <TopMenuButton
                  link="/assessment-types"
                  label="Inspection Tools"
                  closeMenu={closeMenu}
                />
              )}
              <TopMenuButton
                link="/user/pilot"
                label="Pilot Settings"
                closeMenu={closeMenu}
              />
            </div>
            <div
              css={css`
                display: flex;
                flex: 1;
                flex-direction: column;
              `}
            >
              <span className="header-item">Help</span>
              <div css={breakLineStyle} />
              {/* {costCalculator && (
                <TopMenuButton
                  link="/cost-calculator-archived"
                  label="Processing Cost Calculator"
                  closeMenu={closeMenu}
                />
              )} */}
              <a href="https://help.birdi.io/" target="_blank" css={linkStyle}>
                <span>Knowledge Base</span>
                <ExternalLinkSVG className="icon-external" />
              </a>
              <a
                href="https://www.birdi.io/partner-program"
                target="_blank"
                css={linkStyle}
              >
                <span>Partner Program</span>
                <ExternalLinkSVG className="icon-external" />
              </a>
              <a
                href="https://www.birdi.io/terms-and-conditions"
                target="_blank"
                css={linkStyle}
              >
                <span>Terms & Conditions</span>
                <ExternalLinkSVG className="icon-external" />
              </a>
              <div css={breakLineStyle} />
              <button
                type="button"
                onClick={async () => {
                  // Clear session storage to prevent stale state
                  sessionStorage.clear();
                  // Clear any cached plan selection
                  localStorage.removeItem('selectedPlanType');
                  localStorage.removeItem('cancelPlan');
                  localStorage.removeItem('localOldPlan');
                  // Clear authentication cookie and wait for it to complete
                  // Pass false to prevent auto-reload, we'll redirect ourselves
                  await auth.clearCookie(stroot('bsdzx3'), false);
                  // Replace history and force reload to clear bfcache
                  // This prevents back button from showing authenticated pages
                  window.location.replace('/sign-in');
                }}
                css={(theme: BirdiTheme) => css`
                  display: flex;
                  justify-content: flex-start;
                  padding: 0.5em;
                  border-radius: 3px;
                  &:hover {
                    background: ${theme.lightOcean};
                  }
                `}
              >
                <SignOutSVG
                  css={css`
                    stroke-width: 2px;
                    vertical-align: middle;
                    margin-right: 0.5em;
                  `}
                />
                <span
                  css={(theme: BirdiTheme) => css`
                    vertical-align: middle;
                    color: ${theme.midnight};
                    font-weight: bold;
                  `}
                >
                  Log Out
                </span>
              </button>
            </div>
          </div>
          {/* <hr className="divider" /> */}
          <div
            css={(theme: BirdiTheme) => css`
              display: flex;
              flex-direction: row;
              background: ${theme.mono20};
              padding: 0.75em;
            `}
          >
            <a
              href="https://forms.monday.com/forms/0d33a85fe9a0f250f31fe772487c2978?r=use1"
              target="_blank"
              css={linkStyle}
            >
              <SuggestSVG className="icon-comment" />
              <span>Suggest a feature</span>
            </a>
            <a href="mailto:support@birdi.io" css={linkStyle}>
              <ContactUsSVG className="icon-comment" />
              <span>Contact us</span>
            </a>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
